/**
 * A curl for a route no checked sample calls, made from the contract (T13c): the method, the path with its
 * {parameters} to fill, the credential the route takes, and a body of its required fields, each with the
 * schema's own example, default or first value. It is a template, and the page says so.
 */
import type { ApiRoute } from "./api";
import type { RouteCode } from "./route-samples";
import type { Field } from "./schema";

const CREDENTIAL: Record<ApiRoute["auth"], string | null> = {
  key: "$VESTRA_API_KEY",
  key_or_client: "$VESTRA_API_KEY",
  client: "$SESSION_TOKEN",
  public: null,
};

/** A value for a required field: its example, its default, its first allowed value, or a stand-in of its type. */
function valueOf(f: Field): unknown {
  if (f.examples?.length) return f.examples[0];
  if (f.default !== null) {
    try {
      return JSON.parse(f.default);
    } catch {
      return f.default;
    }
  }
  const first = /^"([^"]*)"/.exec(f.type);
  if (first) return first[1];
  if (f.type.endsWith("[]")) return [];
  if (f.type.startsWith("integer") || f.type.startsWith("number")) return 1;
  if (f.type.startsWith("boolean")) return true;
  if (f.type === "object" || f.type.startsWith("map of")) return {};
  return "...";
}

export function curlFor(route: ApiRoute): string {
  const lines: string[] = [];
  const query = route.query
    .filter((q) => q.required)
    .map((q) => `${q.name}=...`);
  const url = `"$VESTRA_BASE_URL${route.path}${query.length ? `?${query.join("&")}` : ""}"`;
  const stream =
    route.answer.media[0] === "text/event-stream" && route.method === "GET";
  lines.push(
    `curl ${route.method === "GET" ? "" : `-X ${route.method} `}${stream ? "-N " : ""}${url}`
  );
  const credential = CREDENTIAL[route.auth];
  if (credential) lines.push(`-H "Authorization: Bearer ${credential}"`);
  if (route.body?.media === "multipart/form-data") {
    lines.push(`-F "file=@ticket.pdf"`);
  } else if (route.body) {
    const fields =
      route.body.shape.kind === "fields"
        ? route.body.shape.fields
        : route.body.shape.kind === "oneOf"
          ? route.body.shape.shapes[0]!.fields
          : [];
    const required = fields.filter((f) => f.depth === 0 && f.required);
    if (required.length > 0 || route.body.required) {
      const body = Object.fromEntries(
        required.map((f) => [f.name, valueOf(f)])
      );
      lines.push(`-H "Content-Type: application/json"`);
      lines.push(
        `-d '${JSON.stringify(body, null, 2).replace(/\n/g, "\n  ")}'`
      );
    }
  }
  return lines.join(" \\\n  ");
}

/** The route over HTTP: the checked sample's call, or one made from the contract. */
export const httpCall = (route: ApiRoute, code: RouteCode): string =>
  code.sample?.doors.find((d) => d.door === "http")?.code ?? curlFor(route);
